import parse from "html-react-parser";
import { useMemo } from "react";
import ReactMarkdown, { Components } from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import {
  vs,
  vscDarkPlus,
} from "react-syntax-highlighter/dist/esm/styles/prism";
import remarkGfm from "remark-gfm";
import * as SharedMessageTypes from "../../../../../../darvin-shared/schema/zod/agent/message-types.schema";
import { useTheme } from "../../../../../providers/ThemeProvider";
import {
  closeUnclosedMarkdownFormatting,
  splitIntoWords,
  splitMarkdownIntoDisplayWords,
} from "../../../../../services/textSplitterService";
import { ProjectChatAssistantMessage } from "../../../../../view_stores/chat_messages/chatModels";
import {
  ProjectAssistantMessageBase,
  RollbackBtnProps,
} from "./ProjectAssistantMessageBase";

// Helper function to transform camelCase to header format
function camelCaseToHeader(camelCase: string): string {
  if (!camelCase) {
    return "";
  }
  return camelCase
    .replace(/([A-Z])/g, " $1") // Add space before capital letters
    .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
    .replace(/\s+([A-Z])/g, (_, letter) => " " + letter.toLowerCase()) // Lowercase subsequent words
    .trim(); // Remove leading/trailing spaces
}

// Helper function to detect if content contains markdown
function containsMarkdown(content: string): boolean {
  const markdownPatterns = [
    /^#{1,6}\s/m, // Headers (multiline)
    /\*\*[^*]+\*\*/, // Bold
    /\*[^*]+\*/, // Italic (but not bold)
    /`[^`]+`/, // Inline code
    /```[\s\S]*?```/, // Code blocks
    /\[[^\]]+\]\([^)]+\)/, // Links
    /^\s*[-*+]\s/m, // Unordered lists (multiline)
    /^\s*\d+\.\s/m, // Ordered lists (multiline)
    /^\s*>\s/m, // Blockquotes (multiline)
    /^\|.*\|$/m, // Tables (multiline)
    /~~[^~]+~~/, // Strikethrough
    /^\s*```[\s\S]*?```$/m, // Fenced code blocks (multiline)
  ];

  // Check if any markdown pattern is found
  const hasMarkdown = markdownPatterns.some((pattern) => pattern.test(content));

  // Also check for common markdown indicators
  const hasMarkdownIndicators =
    content.includes("**") ||
    content.includes("*") ||
    content.includes("`") ||
    content.includes("#") ||
    (content.includes("[") && content.includes("](")) ||
    content.includes("~~");

  return hasMarkdown || hasMarkdownIndicators;
}

interface ProjectAssistantMessageProps {
  message: ProjectChatAssistantMessage;
  rollbackBtn?: RollbackBtnProps;
}

type WordType = { word: string; endIndex: number };

export function ProjectAssistantUnifiedMessage({
  message,
  rollbackBtn,
}: ProjectAssistantMessageProps) {
  const { resolvedTheme } = useTheme();

  function createContent(): { content: string; isMarkdown: boolean } {
    if (message.type === SharedMessageTypes.MessageTypeEnum.Enum.general) {
      const generalMsg =
        message.value as SharedMessageTypes.GeneralAssistantMessage;
      const content = generalMsg.message.join("\n");
      // const header = camelCaseToHeader(generalMsg.topic);
      const isMarkdown = containsMarkdown(content);

      if (isMarkdown) {
        return {
          content: `${content}`,
          isMarkdown: true,
        };
        // return {
        //   content: `**${header}:**\n${content}`,
        //   isMarkdown: true,
        // };
      } else {
        return {
          content: `${content.replace(/\n/g, "<br />")}`,
          isMarkdown: false,
        };
        // return {
        //   content: `<strong>${header}:</strong><br />${content.replace(
        //     /\n/g,
        //     "<br />"
        //   )}`,
        //   isMarkdown: false,
        // };
      }
    } else if (
      message.type === SharedMessageTypes.MessageTypeEnum.Enum.projectConfig
    ) {
      const content = (message.value as SharedMessageTypes.ProjectConfig)
        .message;
      return {
        content: `${content}`,
        isMarkdown: false,
      };
      // return {
      //   content: `<strong>Project config:</strong><br />${content}`,
      //   isMarkdown: false,
      // };
    }
    return { content: "", isMarkdown: false };
  }

  // Memoize content creation to prevent infinite re-renders
  const { content: currentContent, isMarkdown } = useMemo(
    () => createContent(),
    [message]
  );

  // Store split words/indices for animation based on content type
  const words = useMemo(() => {
    if (isMarkdown) {
      return splitMarkdownIntoDisplayWords(currentContent);
    } else {
      return splitIntoWords(currentContent);
    }
  }, [currentContent, isMarkdown, message.id]);

  const components: Components = {
    code({ className, children, node, ...props }) {
      const match = /language-(\w+)/.exec(className || "");
      const language = match ? match[1] : "dart"; // Default to dart if no language specified
      const isInline = node?.position?.start.line === node?.position?.end.line;

      if (isInline) {
        return (
          <code className={className} {...props}>
            {children}
          </code>
        );
      }

      return (
        <SyntaxHighlighter
          // @ts-ignore - Known type mismatch in react-syntax-highlighter props
          style={resolvedTheme === "dark" ? vscDarkPlus : vs}
          language={language}
          customStyle={{
            borderRadius: "0.375rem",
            fontSize: "15px",
            lineHeight: "1.5",
          }}
          codeTagProps={{ style: { fontFamily: "Menlo" } }}
          {...props}
        >
          {String(children).replace(/\n$/, "")}
        </SyntaxHighlighter>
      );
    },
  };

  return (
    <ProjectAssistantMessageBase
      message={message}
      typeWriterAnimationSpeed={
        message.type === SharedMessageTypes.MessageTypeEnum.Enum.general &&
        (message.value as SharedMessageTypes.GeneralAssistantMessage).topic ===
          SharedMessageTypes.GeneralAssistantMessageTopicEnum.Enum.other
          ? 25
          : 50
      }
      words={
        isMarkdown
          ? (words as WordType[]).map((w) => w.word)
          : (words as string[])
      }
      renderContent={(visibleWords) => {
        if (words.length === 0) return null;

        if (isMarkdown) {
          const lastIdx = Math.min(visibleWords, words.length);
          if (lastIdx === 0) return null;
          const partial = currentContent.slice(
            0,
            (words as WordType[])[lastIdx - 1].endIndex
          );
          return (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
              {closeUnclosedMarkdownFormatting(partial)}
            </ReactMarkdown>
          );
        } else {
          return (
            <span>
              {parse((words as string[]).slice(0, visibleWords).join(""))}
            </span>
          );
        }
      }}
      rollbackBtn={rollbackBtn}
    />
  );
}
